/**
 * titleCase — normalize free-form place/topic input for storage
 * and display. Handles multi-word names, hyphenation, and common
 * abbreviations that should stay upper-case (USA, UAE, UK, etc.).
 *
 *   titleCase("thiruvananthapuram")  → "Thiruvananthapuram"
 *   titleCase("new york city")       → "New York City"
 *   titleCase("st. john's")          → "St. John's"
 *   titleCase("USA")                 → "USA"
 *   titleCase("mcdowell county")     → "McDowell County"
 */

// Short tokens that should stay all-caps regardless of position.
const ALL_CAPS = new Set([
  "USA", "UAE", "UK", "USSR", "EU", "US", "DC", "NYC", "LA", "SF", "SFO",
  "NASA", "FEMA", "NOAA", "FBI", "CIA", "DMV", "IRS", "EPA", "DOT", "GDP",
  "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "XI", "XII",
]);

// Short connective words that stay lower-case in titles unless they
// are the first or last word.
const LOWER_JOINERS = new Set([
  "a", "an", "the", "and", "or", "but", "for", "nor", "on", "in", "at",
  "to", "of", "by", "vs", "via",
]);

function capitalizeWord(word, opts = {}) {
  if (!word) return word;
  const upper = word.toUpperCase();
  if (ALL_CAPS.has(upper)) return upper;
  const lower = word.toLowerCase();
  if (!opts.first && !opts.last && LOWER_JOINERS.has(lower)) return lower;
  // Special cases: names starting with Mc / Mac / O' get inner cap.
  if (/^mc[a-z]/i.test(word)) return "Mc" + word.charAt(2).toUpperCase() + word.slice(3).toLowerCase();
  if (/^o'[a-z]/i.test(word)) return "O'" + word.charAt(2).toUpperCase() + word.slice(3).toLowerCase();
  // Hyphenated words → title-case each segment.
  if (word.includes("-")) {
    return word.split("-").map((seg) => capitalizeWord(seg, { first: true })).join("-");
  }
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

export function titleCase(input) {
  if (input == null) return input;
  const s = String(input).trim();
  if (!s) return s;
  const words = s.split(/\s+/);
  return words.map((w, i) => capitalizeWord(w, {
    first: i === 0,
    last:  i === words.length - 1,
  })).join(" ");
}

/** Apply titleCase to every string value in an object, leaving keys
 *  and non-string values untouched. Used when persisting a locality
 *  or place record captured from free-form user input. */
export function titleCaseFields(obj, fields = ["city", "state", "country", "name"]) {
  if (!obj || typeof obj !== "object") return obj;
  const out = { ...obj };
  for (const k of fields) {
    if (typeof out[k] === "string") {
      out[k] = titleCase(out[k]);
    }
  }
  return out;
}

export default titleCase;
